import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { DIMENSIONS, DIM, nearestAnchor } from "../lib/bopta";
import { VBCta } from "../components/VBCta";

const Preview = () => {
  const [v, setV] = useState(75);
  const a = nearestAnchor(DIM.P.questions[0].anchors, v);
  return (
    <div className="panel fade p-7 shadow-[0_24px_60px_-30px_rgba(0,0,0,.25)]" data-testid="hero-preview">
      <p className="kicker">Try the scale · People</p>
      <p className="display mt-4 text-6xl font-extrabold" data-testid="hero-preview-score">{v}<span className="text-lg muted">/100</span></p>
      <p className="mt-1 font-semibold text-[var(--orange)]">{a.t}</p>
      <Slider className="mt-5" value={[v]} max={100} step={1} onValueChange={([x]) => setV(x)} data-testid="hero-preview-slider" />
      <div className="mt-2 flex justify-between text-xs muted"><span>Total reliance</span><span>Full autonomy</span></div>
      <p className="mt-4 text-sm muted">{a.d}</p>
    </div>
  );
};

const STEPS = [
  ["Set your context", "Your role and who you report to. Independence means something different for a junior executive than a head of department."],
  ["Reflect and solve", "Two autonomy questions and one planning-horizon question per BOPTA dimension, then a short Broken Machine business case."],
  ["Get your plan", "Your autonomy profile, mental bandwidth, capacity verdict, an AI 7/30/90-day plan and a script for your manager."],
];

export default function Home() {
  return (
    <>
      <section className="wrap grid items-center gap-12 py-16 lg:grid-cols-2 lg:py-24">
        <div className="fade">
          <p className="kicker">Talent Autonomy Baseline</p>
          <h1 className="mt-4 text-5xl font-extrabold leading-[1.02] lg:text-7xl">Your title says one thing. <span className="text-[var(--teal)]">Your autonomy says another.</span></h1>
          <p className="mt-6 max-w-lg text-lg muted">How independently do you really work? Look beyond your job title to understand your decision-making authority, your reliance on your manager, and your next step forward.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/assessment" className="btn btn-main" data-testid="hero-start">Find my autonomy score <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/framework" className="btn btn-line" data-testid="hero-framework">Explore the framework</Link>
          </div>
          <p className="mt-5 text-xs muted">No login · Private shareable result link</p>
        </div>
        <Preview />
      </section>

      <section className="border-y border-[var(--line)] bg-white">
        <div className="wrap grid gap-8 py-16 md:grid-cols-3">
          {STEPS.map(([t, d], i) => (
            <div key={t} data-testid={`how-step-${i + 1}`}>
              <span className="display text-4xl font-extrabold text-[var(--teal)]">0{i + 1}</span>
              <h3 className="mt-3 text-xl font-bold">{t}</h3>
              <p className="mt-2 text-sm muted">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="wrap py-20">
        <p className="kicker">The BOPTA lens</p>
        <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Five places where work gets stuck.</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {DIMENSIONS.map((d) => (
            <div key={d.k} className="panel p-6" style={{ borderTop: `4px solid ${d.color}` }} data-testid={`home-dim-${d.k}`}>
              <span className="display text-4xl font-extrabold" style={{ color: d.color }}>{d.k}</span>
              <p className="mt-4 font-bold">{d.name}</p>
              <p className="text-xs muted">{d.sub}</p>
              <p className="mt-3 text-sm muted">{d.lead}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="wrap grid gap-6 lg:grid-cols-2">
        <div className="panel flex flex-col justify-between p-8 sm:p-10" data-testid="home-employer-card">
          <div>
            <p className="kicker">For founders, leaders & HR</p>
            <h3 className="mt-3 text-2xl font-bold sm:text-3xl">You see delegation. Your team may see a bottleneck.</h3>
            <p className="mt-3 muted">Create a team, share one link, and compare staff experience with the manager's self-assessment — the 360° perception gap, dimension by dimension.</p>
          </div>
          <Link to="/employers" className="btn btn-main mt-6 self-start" data-testid="home-employer-cta">Set up a team dashboard</Link>
        </div>
        <VBCta />
      </section>
    </>
  );
}
